import React from 'react';
import { ShieldCheck, Workflow, Layers, FileText, Percent, Scale, Info, ChartNoAxesColumn } from 'lucide-react';
import { SkeletonStatCards, SkeletonTable } from '../../../components/common/SkeletonLoader';
import { useComplianceReport, ComplianceBrokerRow } from '../../../hooks/pipelineTracker/useComplianceReport';
import { EmptyState, WarnState, BRAND_BLUE, shortCount } from './trackerUi';
import { TrackerSection } from './TrackerSection';
import { AccentPanel, HouseStatCard as StatCard, HouseStatGrid, RatioBar, ToneBanner } from './trackerKit';

/**
 * Broker file quality.
 *
 * NO GRADES, NO THRESHOLDS, NO PASS AND FAIL. What counts as "enough" products
 * compared or "enough" words written is a compliance judgement this product is
 * not qualified to make, and inventing a threshold would lend it the appearance
 * of one. Figures are shown with their coverage, and the reader decides.
 *
 * Missing counts are shown beside every metric, because only 613 of 2,182 deals
 * in the real export carry a rate: a median without its coverage reads as a
 * statement about the whole book.
 *
 * The colours are per MEASURE, never per value: a bar is drawn in its measure's colour however
 * full it is, so no green or red can imply a grade the paragraph above rules out.
 */

const pct = (bp: number | null): string => (bp === null ? '-' : `${(bp / 100).toFixed(2)}%`);
const num = (n: number | null): string => (n === null ? '-' : n.toLocaleString('en-AU'));

/** One colour per measure, shared by its stat card, its coverage bar and its table bar. */
const MEASURE = {
  deals: '#3b82f6',
  tmd: '#10b981',
  products: '#06b6d4',
  words: '#8b5cf6',
  rate: '#f59e0b',
  lvr: '#64748b',
} as const;

const Missing: React.FC<{ n: number }> = ({ n }) =>
  (n > 0 ? <span className="ml-1 text-xs text-gray-500">({n} missing)</span> : null);

const TH: React.FC<{ children: React.ReactNode; right?: boolean }> = ({ children, right }) => (
  <th className={`sticky top-0 z-10 bg-gray-900 px-4 py-2.5 font-semibold ${right ? 'text-right' : ''}`}>{children}</th>
);

const Row: React.FC<{ r: ComplianceBrokerRow; emphasis?: boolean }> = ({ r, emphasis }) => {
  const tmdRecorded = r.deals - r.tmdMissing;
  return (
    <tr
      className={
        emphasis
          ? 'bg-blue-500/[0.08] font-medium text-gray-100'
          : 'text-gray-300 odd:bg-white/[0.015] transition-colors hover:bg-white/[0.04]'
      }
    >
      <td className="px-4 py-2.5">{r.adviserName}</td>
      <td className="px-4 py-2.5 text-right tabular-nums">{r.deals.toLocaleString('en-AU')}</td>
      <td className="px-4 py-2.5 text-right tabular-nums">
        {num(r.avgProductsCompared)}<Missing n={r.productsComparedMissing} />
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums">
        {num(r.medianWordCount)}<Missing n={r.wordCountMissing} />
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums">
        <div>
          {r.tmdComplete.toLocaleString('en-AU')} of {tmdRecorded.toLocaleString('en-AU')}
          <Missing n={r.tmdMissing} />
        </div>
        {tmdRecorded > 0 && (
          <RatioBar ratio={r.tmdComplete / tmdRecorded} color={MEASURE.tmd} className="ml-auto mt-1 w-24" />
        )}
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums">
        {pct(r.medianInterestRateBp)}
        <span className="ml-1 text-xs text-gray-500">({r.interestRateCoverage} of {r.deals})</span>
      </td>
      <td className="px-4 py-2.5 text-right tabular-nums">
        {pct(r.medianTotalLvrBp)}
        <span className="ml-1 text-xs text-gray-500">({r.totalLvrCoverage} of {r.deals})</span>
      </td>
    </tr>
  );
};

/** How many of the snapshot's deals carry each measure: the coverage the figures stand on. */
const CoverageBars: React.FC<{ company: ComplianceBrokerRow }> = ({ company: c }) => {
  const rows = [
    { label: 'TMD recorded', have: c.deals - c.tmdMissing, colour: MEASURE.tmd },
    { label: 'Products compared recorded', have: c.deals - c.productsComparedMissing, colour: MEASURE.products },
    { label: 'Word count recorded', have: c.deals - c.wordCountMissing, colour: MEASURE.words },
    { label: 'Interest rate recorded', have: c.interestRateCoverage, colour: MEASURE.rate },
    { label: 'LVR recorded', have: c.totalLvrCoverage, colour: MEASURE.lvr },
  ];
  return (
    <ul className="grid gap-x-8 gap-y-3 md:grid-cols-2" data-testid="file-quality-coverage">
      {rows.map((row) => (
        <li key={row.label}>
          <div className="flex items-baseline justify-between gap-3 text-[0.8125rem]">
            <span className="flex items-center gap-2 text-gray-200">
              <span aria-hidden="true" className="h-2 w-2 rounded-full" style={{ background: row.colour }} />
              {row.label}
            </span>
            <span className="tabular-nums text-gray-400">
              {row.have.toLocaleString('en-AU')} of {c.deals.toLocaleString('en-AU')} deals
            </span>
          </div>
          <RatioBar ratio={c.deals > 0 ? row.have / c.deals : 0} color={row.colour} className="mt-1.5 h-2" />
        </li>
      ))}
    </ul>
  );
};

const ComplianceTab: React.FC<{ companyId: string }> = ({ companyId }) => {
  const { result, loading, error } = useComplianceReport(companyId);

  if (loading) {
    return (
      <div className="space-y-6">
        <SkeletonStatCards count={4} columns={4} />
        <SkeletonTable rows={8} columns={7} />
      </div>
    );
  }
  if (error) return <WarnState>{error}</WarnState>;
  if (!result) return null;

  if (result.status === 'no_snapshot') {
    return (
      <EmptyState
        title="No Compliance export yet"
        body="Upload a Compliance export to see file documentation quality by broker. Only the broker-quality columns are stored; the customer financial position columns in that export are never kept."
      />
    );
  }

  const { data } = result;

  return (
    <TrackerSection
      id="ops-file-quality"
      step="1 · File quality"
      title="File documentation measures"
      description="Taken from the Compliance export, with coverage beside every figure."
    >
      <ToneBanner icon={Info} color={BRAND_BLUE}>
        <p className="text-blue-50">
          File documentation measures only, taken from the Compliance export. No customer financial
          information is stored or shown, and no threshold or grade is applied to these figures.
        </p>
        <p className="mt-1 text-xs text-blue-200/80">
          As at {data.asAtDate}. Coverage is shown beside every figure, because a median without its
          denominator reads as a statement about the whole book.
        </p>
      </ToneBanner>

      <HouseStatGrid>
        <StatCard label="Deals" value={shortCount(data.totalDeals)}
          sub="In this snapshot" icon={Workflow} color={MEASURE.deals} />
        <StatCard label="TMD complete"
          value={`${data.company.tmdComplete} of ${data.company.deals - data.company.tmdMissing}`}
          sub={data.company.tmdMissing > 0 ? `${data.company.tmdMissing} not recorded` : 'All recorded'}
          icon={ShieldCheck} color={MEASURE.tmd}
          tone={data.company.tmdMissing > 0 ? 'warn' : 'good'} />
        <StatCard label="Avg products compared" value={num(data.company.avgProductsCompared)}
          sub={data.company.productsComparedMissing > 0 ? `${data.company.productsComparedMissing} missing` : 'Complete'}
          icon={Layers} color={MEASURE.products} />
        <StatCard label="Median word count" value={num(data.company.medianWordCount)}
          sub={data.company.wordCountMissing > 0 ? `${data.company.wordCountMissing} missing` : 'Complete'}
          icon={FileText} color={MEASURE.words} />
        <StatCard label="Median rate" value={pct(data.company.medianInterestRateBp)}
          sub={`${data.company.interestRateCoverage} of ${data.company.deals} deals carry a rate`}
          icon={Percent} color={MEASURE.rate} />
        <StatCard label="Median LVR" value={pct(data.company.medianTotalLvrBp)}
          sub={`${data.company.totalLvrCoverage} of ${data.company.deals} deals carry an LVR`}
          icon={Scale} color={MEASURE.lvr} />
      </HouseStatGrid>

      <AccentPanel
        title="Coverage by measure"
        icon={ChartNoAxesColumn}
        color="#0891b2"
        blurb="How many of the snapshot's deals record each measure. Each bar is drawn in its measure's colour, whatever its length: coverage is context for the figures, not a grade."
      >
        <CoverageBars company={data.company} />
      </AccentPanel>

      <AccentPanel title="File quality by broker" icon={ShieldCheck} color={MEASURE.tmd} flush>
        <div className="max-h-[70vh] overflow-auto">
          <table className="w-full text-left text-sm">
            <thead className="text-[0.6875rem] uppercase tracking-wider text-gray-400">
              <tr>
                <TH>Broker</TH>
                <TH right>Deals</TH>
                <TH right>Avg products compared</TH>
                <TH right>Median word count</TH>
                <TH right>TMD complete</TH>
                <TH right>Median rate</TH>
                <TH right>Median LVR</TH>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              <Row r={data.company} emphasis />
              {data.brokers.map((b) => <Row key={b.adviserId} r={b} />)}
            </tbody>
          </table>
        </div>
      </AccentPanel>
    </TrackerSection>
  );
};

export default ComplianceTab;
